import { useEffect, useState, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { KpiCard } from "@/components/KpiCard";
import { DatePicker } from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { fmtDA, fmtDate, todayISO } from "@/lib/format";
import { Wallet, Plus, ArrowLeftRight, Loader2, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = {
  encaissement: "Encaissement", versement: "Versement", retrait: "Retrait",
  depense: "Dépense", paiement_fournisseur: "Paiement fournisseur",
  transfert_caisse_banque: "Transfert → banque", transfert_banque_caisse: "Transfert ← banque",
  annulation_depense: "Annulation dépense",
};

export default function Caisse() {
  const { societes, societeParam, societeId } = useApp();
  const [items, setItems] = useState([]);
  const [solde, setSolde] = useState(0);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [transfer, setTransfer] = useState(false);
  const [comptes, setComptes] = useState([]);
  const [form, setForm] = useState(null);
  const [tf, setTf] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/caisse/mouvements", { params: societeParam });
      setItems(data.items);
      setSolde(data.solde);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [societeId]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const sid = () => (societeId !== "all" ? societeId : societes[0]?.id || "");

  const openNew = () => { setForm({ societe_id: sid(), date: todayISO(), type: "encaissement", sens: "entree", montant: "", description: "" }); setOpen(true); };
  const openTransfer = () => {
    const s = sid();
    setTf({ societe_id: s, date: todayISO(), montant: "", compte_id: "", sens: "caisse_vers_banque", description: "" });
    if (s) api.get("/banque/comptes", { params: { societe_id: s } }).then((r) => setComptes(r.data));
    setTransfer(true);
  };

  const submit = async () => {
    if (!form.societe_id) return toast.error("Société requise.");
    if (!form.montant || Number(form.montant) <= 0) return toast.error("Montant invalide.");
    const sens = form.type === "encaissement" || form.type === "versement" ? "entree" : "sortie";
    try {
      await api.post("/caisse/mouvements", { ...form, sens, montant: Number(form.montant) });
      toast.success("Mouvement enregistré.");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitTransfer = async () => {
    if (!tf.compte_id) return toast.error("Compte bancaire requis.");
    if (!tf.montant || Number(tf.montant) <= 0) return toast.error("Montant invalide.");
    try {
      await api.post("/caisse/transfert", { ...tf, montant: Number(tf.montant) });
      toast.success("Transfert effectué.");
      setTransfer(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader
        title="Caisse"
        subtitle="Mouvements en espèces et solde de caisse"
        testid="caisse-page"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={openTransfer} className="gap-2" data-testid="transfer-btn"><ArrowLeftRight className="h-4 w-4" /> Transfert</Button>
            <Button onClick={openNew} className="gap-2" data-testid="new-mouvement-btn"><Plus className="h-4 w-4" /> Mouvement</Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 kpi-grid">
        <KpiCard testid="caisse-solde" label="Solde de caisse" value={solde} icon={Wallet} tone={solde >= 0 ? "success" : "danger"} />
        <KpiCard testid="caisse-entrees" label="Total entrées" value={items.filter((m) => m.sens === "entree").reduce((s, m) => s + m.montant, 0)} icon={ArrowDownLeft} tone="success" />
        <KpiCard testid="caisse-sorties" label="Total sorties" value={items.filter((m) => m.sens === "sortie").reduce((s, m) => s + m.montant, 0)} icon={ArrowUpRight} tone="danger" />
      </div>

      <div className="rounded-xl border bg-card overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : items.length === 0 ? (
          <EmptyState icon={Wallet} title="Aucun mouvement" hint="Les dépenses en espèces et encaissements alimentent la caisse automatiquement." />
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="caisse-table">
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Montant</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell className="tabular text-sm whitespace-nowrap">{fmtDate(m.date)}</TableCell>
                    <TableCell className="text-sm">{TYPE_LABEL[m.type] || m.type}</TableCell>
                    <TableCell className="text-sm max-w-[320px] truncate">{m.description || "—"}</TableCell>
                    <TableCell className={`tabular text-right font-semibold whitespace-nowrap ${m.sens === "entree" ? "text-success" : "text-destructive"}`}>
                      {m.sens === "entree" ? "+" : "−"} {fmtDA(m.montant)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover max-w-md" data-testid="mouvement-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Nouveau mouvement de caisse</DialogTitle></DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Société</Label>
                <Select value={form.societe_id} onValueChange={(v) => setForm((f) => ({ ...f, societe_id: v }))}>
                  <SelectTrigger data-testid="mv-societe"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent className="bg-popover">{societes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Date</Label><DatePicker value={form.date} onChange={(v) => setForm((f) => ({ ...f, date: v }))} testid="mv-date" /></div>
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                  <SelectTrigger data-testid="mv-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    <SelectItem value="encaissement">Encaissement (+)</SelectItem>
                    <SelectItem value="versement">Versement (+)</SelectItem>
                    <SelectItem value="retrait">Retrait (−)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Montant *</Label><Input data-testid="mv-montant" type="number" step="0.01" value={form.montant} onChange={(e) => setForm((f) => ({ ...f, montant: e.target.value }))} /></div>
              <div className="space-y-1.5 col-span-2"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} data-testid="mv-submit">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={transfer} onOpenChange={setTransfer}>
        <DialogContent className="bg-popover max-w-md" data-testid="transfert-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Transfert caisse ↔ banque</DialogTitle></DialogHeader>
          {tf && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5 col-span-2">
                <Label>Sens</Label>
                <Select value={tf.sens} onValueChange={(v) => setTf((f) => ({ ...f, sens: v }))}>
                  <SelectTrigger data-testid="tf-sens"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    <SelectItem value="caisse_vers_banque">Caisse → Banque</SelectItem>
                    <SelectItem value="banque_vers_caisse">Banque → Caisse</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Compte bancaire</Label>
                <Select value={tf.compte_id} onValueChange={(v) => setTf((f) => ({ ...f, compte_id: v }))}>
                  <SelectTrigger data-testid="tf-compte"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent className="bg-popover">{comptes.map((c) => <SelectItem key={c.id} value={c.id}>{c.banque} · {c.nom_compte}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Montant *</Label><Input data-testid="tf-montant" type="number" step="0.01" value={tf.montant} onChange={(e) => setTf((f) => ({ ...f, montant: e.target.value }))} /></div>
              <div className="space-y-1.5 col-span-2"><Label>Date</Label><DatePicker value={tf.date} onChange={(v) => setTf((f) => ({ ...f, date: v }))} testid="tf-date" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransfer(false)}>Annuler</Button>
            <Button onClick={submitTransfer} data-testid="tf-submit">Transférer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
